import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import {
    hasInternalAdminPrivileges,
    type ActiveRoleAssignmentResponse,
    type CloverWebhookProcessingResult,
} from '@pillar-portal/api-types';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { authApi, paymentsApi } from '@/lib/pillarApi';
import { getActiveRoleAssignmentOperationalResponse } from '@/lib/active-role-assignment';

function describeError(error: unknown): string {
    return error instanceof Error
        ? error.message
        : 'Failed to process outstanding Clover webhooks.';
}

export function CloverWebhooksContent(): ReactElement {
    const activeRoleAssignmentQuery = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await authApi.getActiveRoleAssignment(),
        staleTime: 30_000,
    });
    const processMutation = useMutation<CloverWebhookProcessingResult, Error>({
        mutationFn: async () => await paymentsApi.processOutstandingCloverWebhooks(),
    });

    const canProcess: boolean = hasInternalAdminPrivileges(
        getActiveRoleAssignmentOperationalResponse(activeRoleAssignmentQuery.data)
    );

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold">Clover Webhooks</h1>
            <Card>
                <CardHeader>
                    <CardTitle>Outstanding Messages</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {activeRoleAssignmentQuery.isLoading ? (
                        <p>Checking permissions...</p>
                    ) : activeRoleAssignmentQuery.isError ? (
                        <Alert variant="destructive">
                            <AlertDescription className="space-y-3">
                                <p>
                                    {describeError(activeRoleAssignmentQuery.error)} Unable to
                                    verify permission to process Clover webhooks.
                                </p>
                                <Button
                                    variant="outline"
                                    onClick={() => void activeRoleAssignmentQuery.refetch()}
                                >
                                    Retry permission check
                                </Button>
                            </AlertDescription>
                        </Alert>
                    ) : !canProcess ? (
                        <Alert variant="destructive">
                            <AlertDescription>
                                You do not have permission to process Clover webhooks.
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <>
                            <p className="text-sm text-muted-foreground">
                                Retry up to 100 pending or interrupted Clover webhook messages.
                            </p>
                            <Button
                                onClick={() => processMutation.mutate()}
                                disabled={processMutation.isPending}
                            >
                                {processMutation.isPending
                                    ? 'Processing Clover webhooks...'
                                    : 'Process outstanding Clover webhooks'}
                            </Button>
                            {processMutation.error !== null ? (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {describeError(processMutation.error)} Try again when the
                                        failure has been resolved.
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                            {processMutation.data !== undefined ? (
                                <Alert>
                                    <AlertDescription>
                                        Attempted {processMutation.data.attempted}; processed{' '}
                                        {processMutation.data.processed}; failed{' '}
                                        {processMutation.data.failed}.
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export default function CloverWebhooksPage(): ReactElement {
    return (
        <ProtectedRoute>
            <MainLayout>
                <CloverWebhooksContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
